import { z } from "zod";
import type { CurrencyAmountBuckets } from "./reports";

export const PerformanceDashboardRequestRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  createdAt: z.coerce.date(),
});

export const PerformanceDashboardEventRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  eventDate: z.coerce.date(),
  locationText: z.string().nullable(),
});

export const PerformanceDashboardDocumentRowSchema = z.object({
  id: z.string(),
  documentNo: z.string(),
  documentType: z.enum(["quotation", "invoice", "receipt"]),
  status: z.string(),
  eventId: z.string(),
  eventTitle: z.string().nullable(),
});

export const PerformanceDashboardPayoutRowSchema = z.object({
  id: z.string(),
  payoutNo: z.string(),
  status: z.string(),
  performerName: z.string().nullable(),
  eventTitle: z.string().nullable(),
  totalAmount: z.number(),
});

const CurrencyAmountBucketsSchema = z.object({
  LAK: z.number(),
  USD: z.number(),
  THB: z.number(),
}) satisfies z.ZodType<CurrencyAmountBuckets>;

export const PerformanceOperationsDashboardKpisSchema = z.object({
  openRequests: z.number().optional(),
  openRequestsOverdue: z.number().optional(),
  wonAwaitingConvert: z.number().optional(),
  wonAwaitingConvertStale: z.number().optional(),
  upcomingEvents: z.number().optional(),
  documentsPendingAction: z.number().optional(),
  payoutsPendingAction: z.number().optional(),
  invoiced: CurrencyAmountBucketsSchema.optional(),
  collected: CurrencyAmountBucketsSchema.optional(),
  outstandingAr: CurrencyAmountBucketsSchema.optional(),
  payoutOutstanding: z.number().optional(),
});

export const PerformanceOperationsDashboardSchema = z.object({
  kpis: PerformanceOperationsDashboardKpisSchema,
  myRequests: z.array(PerformanceDashboardRequestRowSchema).optional(),
  upcomingEvents: z.array(PerformanceDashboardEventRowSchema).optional(),
  pendingDocuments: z.array(PerformanceDashboardDocumentRowSchema).optional(),
  pendingPayouts: z.array(PerformanceDashboardPayoutRowSchema).optional(),
});

export type PerformanceDashboardRequestRowDTO = z.infer<
  typeof PerformanceDashboardRequestRowSchema
>;
export type PerformanceDashboardEventRowDTO = z.infer<
  typeof PerformanceDashboardEventRowSchema
>;
export type PerformanceDashboardDocumentRowDTO = z.infer<
  typeof PerformanceDashboardDocumentRowSchema
>;
export type PerformanceDashboardPayoutRowDTO = z.infer<
  typeof PerformanceDashboardPayoutRowSchema
>;
export type PerformanceOperationsDashboardKpisDTO = z.infer<
  typeof PerformanceOperationsDashboardKpisSchema
>;
export type PerformanceOperationsDashboardDTO = z.infer<
  typeof PerformanceOperationsDashboardSchema
>;

export type PerformanceDashboardAccess = {
  canRequests: boolean;
  canEvents: boolean;
  canDocuments: boolean;
  canPayouts: boolean;
  canReports: boolean;
};

export function buildPerformanceDashboardAccess(
  permissions: string[],
): PerformanceDashboardAccess {
  return {
    canRequests: permissions.includes("performance-requests:read"),
    canEvents: permissions.includes("performance-events:read"),
    canDocuments: permissions.includes("performance-documents:read"),
    canPayouts: permissions.includes("performance-payouts:read"),
    canReports: permissions.includes("performance-reports:read"),
  };
}
